/**
 * Leviathan Web - Wizard Component
 * Quasar Prime LLC
 * Reusable multi-step form wizard with progress indicator
 */

import { useState, useEffect, ReactNode } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle } from 'lucide-react';

export interface WizardStep {
  id: string;
  title: string;
  description?: string;
  content: ReactNode;
  isValid?: boolean;
  isOptional?: boolean;
}

interface WizardProps {
  steps: WizardStep[];
  onComplete: () => void;
  onCancel?: () => void;
  title?: string;
  subtitle?: string;
  isSubmitting?: boolean;
  submitLabel?: string;
  /**
   * Jump to the step with this id.
   *
   * Exists so a submission that fails validation can put the operator in
   * front of the field that failed. A wizard hides most of its own form, so
   * "Contact Email is invalid" shown on the last step is not actionable if
   * the contact email lives two steps back — the message points somewhere the
   * operator cannot see.
   *
   * Carries a nonce rather than being a plain id so that failing twice on the
   * SAME field still navigates the second time.
   */
  jumpToStep?: { stepId: string; nonce: number } | null;
}

export function Wizard({
  steps,
  onComplete,
  onCancel,
  title,
  subtitle,
  isSubmitting = false,
  submitLabel = 'Complete',
  jumpToStep = null,
}: WizardProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const jumpNonce = jumpToStep ? jumpToStep.nonce : null;
  const jumpStepId = jumpToStep ? jumpToStep.stepId : null;
  useEffect(() => {
    if (jumpStepId === null) return;
    const index = steps.findIndex((step) => step.id === jumpStepId);
    if (index >= 0) setCurrentStepIndex(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jumpNonce, jumpStepId]);

  // Guard against empty steps array
  if (steps.length === 0) {
    return <div className="text-text-tertiary font-mono">No steps configured</div>;
  }

  const currentStep = steps[currentStepIndex]!;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const canProceed = currentStep.isValid !== false;

  const handleNext = () => {
    if (!isLastStep && canProceed) {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStepIndex((prev) => prev - 1);
    }
  };

  const handleComplete = () => {
    if (canProceed) {
      onComplete();
    }
  };

  const goToStep = (index: number) => {
    // Only allow going to previous steps or current step
    if (index <= currentStepIndex) {
      setCurrentStepIndex(index);
    }
  };

  return (
    <div className="min-h-[500px] flex flex-col">
      {/* Header */}
      {(title || subtitle) && (
        <div className="mb-6 pb-4 border-b border-panel-border">
          {title && (
            <h2
              className="text-2xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-text-secondary font-mono text-sm mt-1">{subtitle}</p>
          )}
        </div>
      )}

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const isUpcoming = index > currentStepIndex;

            return (
              <div key={step.id} className="flex items-center flex-1">
                {/* Step Circle */}
                <button
                  onClick={() => goToStep(index)}
                  disabled={isUpcoming}
                  className={`
                    relative flex items-center justify-center w-10 h-10 rounded-full border-2 font-mono font-bold text-sm transition-all
                    ${isCompleted ? 'bg-success-green border-success-green text-base-dark cursor-pointer' : ''}
                    ${isCurrent ? 'bg-amber border-amber text-base-dark' : ''}
                    ${isUpcoming ? 'bg-transparent border-panel-border text-text-tertiary cursor-not-allowed' : ''}
                    ${!isUpcoming && !isCurrent ? 'hover:border-amber' : ''}
                  `}
                >
                  {isCompleted ? <Check size={18} /> : index + 1}
                </button>

                {/* Step Label */}
                <div className="ml-3 hidden sm:block">
                  <p
                    className={`font-mono text-sm font-semibold ${
                      isCurrent ? 'text-amber' : isCompleted ? 'text-text-primary' : 'text-text-tertiary'
                    }`}
                  >
                    {step.title}
                    {step.isOptional && (
                      <span className="text-text-tertiary font-normal ml-1">(optional)</span>
                    )}
                  </p>
                  {step.description && (
                    <p className="text-text-tertiary text-xs font-mono">{step.description}</p>
                  )}
                </div>

                {/* Connector Line */}
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 transition-colors ${
                      index < currentStepIndex ? 'bg-success-green' : 'bg-panel-border'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="flex-1 bg-panel-dark border border-panel-border p-6">
        {/* Step Header */}
        <div className="mb-6 pb-4 border-b border-amber">
          <h3 className="text-lg font-display font-bold text-text-primary uppercase tracking-wide">
            Step {currentStepIndex + 1}: {currentStep.title}
          </h3>
          {currentStep.description && (
            <p className="text-text-secondary font-mono text-sm mt-1">
              {currentStep.description}
            </p>
          )}
        </div>

        {/* Validation Warning */}
        {currentStep.isValid === false && (
          <div className="mb-4 p-3 bg-error-red/10 border border-error-red/30 flex items-center gap-2">
            <AlertCircle size={16} className="text-error-red flex-shrink-0" />
            <span className="text-error-red font-mono text-sm">
              Please complete all required fields before proceeding
            </span>
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-[300px]">{currentStep.content}</div>
      </div>

      {/* Navigation Footer */}
      <div className="mt-6 flex items-center justify-between">
        <div>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-text-tertiary hover:text-text-primary font-mono text-sm transition-colors"
            >
              Cancel
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Previous Button */}
          {!isFirstStep && (
            <button
              onClick={handlePrevious}
              className="px-4 py-2 bg-panel-dark border border-panel-border hover:border-amber text-text-secondary hover:text-text-primary font-mono text-sm uppercase tracking-wider transition-all flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
          )}

          {/* Next/Complete Button */}
          {isLastStep ? (
            <button
              onClick={handleComplete}
              disabled={!canProceed || isSubmitting}
              className={`
                px-6 py-2 font-mono text-sm uppercase tracking-wider transition-all flex items-center gap-2
                ${
                  canProceed && !isSubmitting
                    ? 'bg-success-green hover:bg-success-green/80 text-base-dark'
                    : 'bg-panel-dark border border-panel-border text-text-tertiary cursor-not-allowed'
                }
              `}
            >
              {isSubmitting ? (
                <>
                  <span className="animate-pulse">Processing...</span>
                </>
              ) : (
                <>
                  <Check size={16} />
                  {submitLabel}
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`
                px-6 py-2 font-mono text-sm uppercase tracking-wider transition-all flex items-center gap-2
                ${
                  canProceed
                    ? 'bg-amber hover:bg-amber-light text-base-dark'
                    : 'bg-panel-dark border border-panel-border text-text-tertiary cursor-not-allowed'
                }
              `}
            >
              Next
              <ChevronRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Step Indicator (Mobile) */}
      <div className="mt-4 text-center sm:hidden">
        <span className="text-text-tertiary font-mono text-xs">
          Step {currentStepIndex + 1} of {steps.length}
        </span>
      </div>
    </div>
  );
}

export default Wizard;
